import * as React from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { cn } from "../../lib/utils";

const TestimonialCarousel = React.forwardRef(
  ({ className, testimonials, showArrows = true, showDots = true, ...props }, ref) => {
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [exitX, setExitX] = React.useState(0);
    const [isCompact, setIsCompact] = React.useState(false);
    const len = testimonials?.length ?? 0;

    const go = (dir) => {
      if (len <= 1) return;
      setExitX(dir * 40);
      window.setTimeout(() => {
        setCurrentIndex((prev) => (prev + dir + len) % len);
        setExitX(0);
      }, 180);
    };

    const handleDragEnd = (_event, info) => {
      if (len <= 1) return;
      if (Math.abs(info.offset.x) > 80) {
        go(info.offset.x > 0 ? -1 : 1);
      }
    };

    React.useEffect(() => {
      const media = window.matchMedia("(max-width: 767px)");
      const onChange = () => setIsCompact(media.matches);
      onChange();
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }, []);

    if (!len) return null;

    return (
      <div
        ref={ref}
        className={cn("relative flex w-full flex-col items-center justify-center py-4", className)}
        {...props}
      >
        <div className="relative min-h-[410px] w-full max-w-2xl sm:min-h-[340px]">
          {testimonials.map((testimonial, index) => {
            const isCurrentCard = index === currentIndex;
            const isPrevCard = index === (currentIndex + 1) % len;
            const isNextCard = index === (currentIndex + 2) % len;

            if (isCompact && !isCurrentCard) return null;
            if (!isCompact && !isCurrentCard && !isPrevCard && !isNextCard) return null;

            const displayName = testimonial.name?.trim() || "Client";

            return (
              <motion.div
                key={testimonial.id}
                className={cn(
                  "absolute left-1/2 top-0 w-[92%] max-w-lg -translate-x-1/2 cursor-grab rounded-2xl border border-white/[0.08] bg-surface-card p-6 shadow-card active:cursor-grabbing sm:w-full sm:p-8",
                  isCompact && "!relative !left-0 !w-full !max-w-none !translate-x-0"
                )}
                style={{
                  zIndex: isCurrentCard ? 3 : isPrevCard ? 2 : 1,
                }}
                drag={isCurrentCard ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.65}
                onDragEnd={isCurrentCard ? handleDragEnd : undefined}
                initial={{
                  scale: 0.96,
                  opacity: 0,
                  y: isCurrentCard ? 0 : isPrevCard ? 10 : 20,
                  rotate: isCurrentCard ? 0 : isPrevCard ? -2 : -3,
                }}
                animate={{
                  scale: isCompact ? 1 : isCurrentCard ? 1 : 0.96,
                  opacity: isCompact ? 1 : isCurrentCard ? 1 : isPrevCard ? 0.55 : 0.28,
                  x: isCurrentCard ? exitX : 0,
                  y: isCompact ? 0 : isCurrentCard ? 0 : isPrevCard ? 10 : 20,
                  rotate: isCompact ? 0 : isCurrentCard ? exitX / 25 : isPrevCard ? -2 : -3,
                }}
                transition={{
                  type: "spring",
                  stiffness: 320,
                  damping: 26,
                }}
              >
                {showArrows && !isCompact && isCurrentCard && len > 1 && (
                  <div className="absolute inset-x-0 top-4 hidden justify-between px-2 sm:flex">
                    <button
                      type="button"
                      aria-label="Témoignage précédent"
                      className="rounded-lg p-2 text-zinc-500 transition hover:bg-white/[0.06] hover:text-brand"
                      onClick={() => go(-1)}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      aria-label="Témoignage suivant"
                      className="rounded-lg p-2 text-zinc-500 transition hover:bg-white/[0.06] hover:text-brand"
                      onClick={() => go(1)}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                )}

                <div className="flex flex-col items-center gap-5 pt-7 sm:pt-8">
                  <Quote className="h-8 w-8 text-brand/40" aria-hidden />
                  <p className="text-center text-sm leading-relaxed text-zinc-400 sm:text-[15px] md:text-base">
                    « {testimonial.description} »
                  </p>
                  <div className="flex flex-col items-center gap-1">
                    <img
                      src={testimonial.avatar}
                      alt=""
                      className="h-14 w-14 rounded-full border border-white/10 object-cover"
                      loading="lazy"
                    />
                    <h3 className="font-display text-base font-semibold text-white">{displayName}</h3>
                    {testimonial.role ? (
                      <p className="text-xs text-zinc-600">{testimonial.role}</p>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            );
          })}

          {showDots && len > 1 && (
            <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  aria-label={`Afficher le témoignage ${index + 1}`}
                  onClick={() => setCurrentIndex(index)}
                  className={cn(
                    "h-2 rounded-full transition-all",
                    index === currentIndex ? "w-6 bg-brand" : "w-2 bg-zinc-700 hover:bg-zinc-600"
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
);
TestimonialCarousel.displayName = "TestimonialCarousel";

export { TestimonialCarousel };
